import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Badges — soft tinted chips: tinted fill · medium weight · full pill · no drop
 * shadow.
 *
 * Text colour is per-variant, not "the same hue as the fill" — that recipe is
 * what put `warning` at 2.97:1 (RUT-3732). Warning now uses the four-token
 * treatment; see `.github/docs/design-system.md` § Where the colour goes.
 *
 * KNOWN, PRE-EXISTING: `bg-primary/12` and `bg-destructive/12` below are off
 * Tailwind's five-step opacity scale, so they emit no CSS and those two
 * variants render with no fill. Fixing them changes the look of every default
 * and destructive badge, so it is deliberately not folded into RUT-3732.
 *
 * Status vocabulary:
 *   active / in-progress → default (primary)
 *   completed / paid     → success
 *   attention / pending  → warning
 *   failed               → destructive
 *   draft / neutral      → secondary
 *   inactive             → outline
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.72rem] font-medium leading-normal transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-0 bg-primary/12 text-primary',
        secondary:
          'border-0 bg-stone-100 text-stone-600 ring-1 ring-inset ring-stone-200 dark:bg-stone-800 dark:text-stone-200 dark:ring-0',
        destructive:
          'border-0 bg-destructive/12 text-destructive',
        success:
          'border-0 bg-success/15 text-success',
        // RUT-3732: hue in the ring, near-black-warm text on a wash. The old
        // `bg-warning/15 text-warning` was 2.97:1 and failed WCAG AA.
        warning:
          'border-0 bg-warning-subtle text-warning-strong ring-1 ring-inset ring-warning-border',
        outline:
          'border-0 bg-transparent text-stone-500 ring-1 ring-inset ring-stone-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Optional status dot — DS pattern for Active / Completed / Pending chips. */
  dot?: boolean;
}

function Badge({ className, variant, dot, children, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            variant === 'success' && 'bg-success',
            variant === 'warning' && 'bg-warning',
            variant === 'destructive' && 'bg-destructive',
            variant === 'secondary' && 'bg-stone-400',
            variant === 'outline' && 'bg-stone-400',
            (!variant || variant === 'default') && 'bg-primary',
          )}
          aria-hidden
        />
      )}
      {children}
    </div>
  );
}

export { Badge, badgeVariants };
